import { createServerFn } from "@tanstack/react-start";
import { createClient } from "@supabase/supabase-js";

/**
 * AK Prisindex — månadsvis prisnivå för husbil och husvagn, räknad på de
 * annonser vi själva följer dygnet runt (vehicle_snapshots).
 *
 * Mätpunkterna skapas i databasen av `public.snapshot_price_index()` (nattlig
 * cron), så sidan läser bara färdiga rader. Det gör historiken oföränderlig:
 * en publicerad månad räknas aldrig om i efterhand, vilket är hela poängen med
 * ett index.
 */

export interface PriceIndexMonth {
  month: string;
  category: "husbil" | "husvagn";
  n: number;
  medianPrice: number;
  p15Price: number;
  p85Price: number;
  soldN: number;
  soldMedianPrice: number | null;
  /** Index där första publicerade månaden = 100. */
  index: number;
  /** Förändring mot föregående månad i procent, null för första punkten. */
  changePct: number | null;
}

export interface PriceIndexData {
  updatedAt: string;
  baseMonth: string | null;
  months: PriceIndexMonth[];
  latest: Record<"husbil" | "husvagn", PriceIndexMonth | null>;
}

export const getPriceIndex = createServerFn({ method: "GET" }).handler(
  async (): Promise<PriceIndexData> => {
    const empty: PriceIndexData = {
      updatedAt: new Date().toISOString(),
      baseMonth: null,
      months: [],
      latest: { husbil: null, husvagn: null },
    };

    const supabase = createClient(
      process.env["SUPABASE_URL"]!,
      process.env["SUPABASE_PUBLISHABLE_KEY"]!,
      { auth: { storage: undefined, persistSession: false, autoRefreshToken: false } },
    );

    const { data, error } = await supabase
      .from("price_index_months")
      .select("month, category, n, median_price, p15_price, p85_price, sold_n, sold_median_price, updated_at")
      .eq("segment", "alla")
      .in("category", ["husbil", "husvagn"])
      .order("month", { ascending: true })
      .limit(240);

    if (error || !data?.length) return empty;

    const rows = data as {
      month: string;
      category: "husbil" | "husvagn";
      n: number;
      median_price: number;
      p15_price: number;
      p85_price: number;
      sold_n: number | null;
      sold_median_price: number | null;
      updated_at: string;
    }[];

    const base: Partial<Record<"husbil" | "husvagn", number>> = {};
    const prev: Partial<Record<"husbil" | "husvagn", number>> = {};
    const months: PriceIndexMonth[] = [];

    for (const r of rows) {
      base[r.category] ??= r.median_price;
      const baseline = base[r.category]!;
      const previous = prev[r.category];
      months.push({
        month: r.month,
        category: r.category,
        n: r.n,
        medianPrice: r.median_price,
        p15Price: r.p15_price,
        p85Price: r.p85_price,
        soldN: r.sold_n ?? 0,
        soldMedianPrice: r.sold_median_price ?? null,
        index: Math.round((r.median_price / baseline) * 1000) / 10,
        changePct:
          previous != null ? Math.round(((r.median_price / previous - 1) * 100) * 10) / 10 : null,
      });
      prev[r.category] = r.median_price;
    }

    const latestFor = (category: "husbil" | "husvagn") =>
      [...months].reverse().find((m) => m.category === category) ?? null;

    return {
      updatedAt: rows[rows.length - 1]!.updated_at ?? new Date().toISOString(),
      baseMonth: rows[0]!.month,
      months,
      latest: { husbil: latestFor("husbil"), husvagn: latestFor("husvagn") },
    };
  },
);
